import { defineStore } from 'pinia'
import {
  getInfo,
  getUserInfo,
  login,
  loginByToken,
  getStoreTheme,
  getAreaCodeListApi,
  loginByPassword,
  loginBySmsVerifyCode
} from '@/api/user'
import { setLocal, getLocal } from '@/common/js/utils'
import router from '../router'
import aes from '@/utils/aes'
import { showToast } from 'vant'
import usePage from '@/hooks/usePage'
import { app } from '../main'

const { navigateToLogin } = usePage()
export const useUserStore = defineStore({
  id: 'user',
  state: () => ({
    url: '',
    token: '',
    projectId: '',
    appVersion: '',
    secretDate: 0,
    userInfo: {},
    isSetPayPassword: false, //是否设置密码
    isNeedPayPassword: true, //是否需要密码操作
    remarkRequired: false, //备注是否必填
    mobile: '',
    projectInfo: {},
    wxInfo: {},
    settingInfo: {},
    theme: {},
    areaCodeList: []
  }),
  getters: {
    getUrlState() {
      return this.url || getLocal('url')
    },
    getTokenState() {
      return this.token || getLocal('token')
    },
    getProjectIdState() {
      return this.projectId || getLocal('projectId')
    },
    getProjectInfo() {
      return Object.keys(this.projectInfo).length > 0 ? this.projectInfo : JSON.parse(getLocal('projectInfo'))
    },
    getWxInfo() {
      return Object.keys(this.wxInfo).length > 0 ? this.wxInfo : JSON.parse(getLocal('wxInfo'))
    },
    getSettingInfo() {
      return Object.keys(this.settingInfo).length > 0 ? this.settingInfo : JSON.parse(getLocal('settingInfo'))
    },
    getDepositName() {
      return this.getSettingInfo?.depositName || '预存款'
    },
    getAppVersionState() {
      return this.appVersion || getLocal('appVersion')
    },
    getUserInfo() {
      return Object.keys(this.userInfo).length > 0 ? this.userInfo : JSON.parse(getLocal('userInfo'))
    },
    getSetPayPassword() {
      return this.isSetPayPassword || getLocal('isSetPayPassword') === 'true'
    },
    getTheme() {
      return Object.keys(this.theme).length > 0 ? this.theme : JSON.parse(getLocal('theme'))
    },
    getAreaCodeList() {
      return JSON.parse(getLocal('areaCodeList'))
    },
    //是否为微信测试账户
    isRiskComplaint() {
      return (Object.keys(this.userInfo).length > 0 ? this.userInfo : JSON.parse(getLocal('userInfo'))).isRiskComplaint
    },
    getNeedPayPassword() {
      return this.isNeedPayPassword || getLocal('isNeedPayPassword') === 'true'
    },
    getIsRemarkRequired() {
      return this.remarkRequired || getLocal('remarkRequired') === 'true'
    }
  },
  actions: {
    setRemarkRequired(value) {
      this.remarkRequired = value
      setLocal('remarkRequired', value)
    },
    setNeedPayPassword(value) {
      this.isNeedPayPassword = value
      setLocal('isNeedPayPassword', value)
    },
    setPayPassword(value) {
      this.isSetPayPassword = value
      setLocal('isSetPayPassword', value)
    },
    setUrl(value) {
      this.url = value
      setLocal('url', value)
    },
    setToken(value) {
      this.token = value
      setLocal('token', value)
    },
    setProjectId(value) {
      this.projectId = value
      setLocal('projectId', value)
    },
    setAppVersion(value) {
      this.appVersion = value
      setLocal('appVersion', value)
    },
    setUserInfo(value) {
      this.userInfo = value
      setLocal('userInfo', JSON.stringify(value))
    },
    setProjectInfo(value) {
      this.projectInfo = value
      setLocal('projectInfo', JSON.stringify(value))
      // 更新全局积分名称变量
      if (app) {
        app.config.globalProperties.integralName = value?.integralName || '积分'
      }
    },
    setWxInfo(value) {
      this.wxInfo = value
      setLocal('wxInfo', JSON.stringify(value))
    },
    setSettingInfo(value) {
      this.settingInfo = value
      setLocal('settingInfo', JSON.stringify(value))
    },
    setTheme(value) {
      this.theme = value
      setLocal('theme', JSON.stringify(value))
    },
    setSecretDate(value) {
      this.secretDate = value
      setLocal('secretDate', value)
    },
    setAreaCodeList(value) {
      this.areaCodeList = value
      setLocal('areaCodeList', value)
    },
    // 重置userStore
    reset(state) {
      this.token = ''
      localStorage.removeItem('token')
      router.replace('/login')
    },
    async login({ params, loginType }) {
      console.log('params', params)
      return new Promise(async (resolve, reject) => {
        try {
          const { token, project_id: projectId } =
            loginType === 'pwd' ? await loginByPassword(params) : await loginBySmsVerifyCode(params)
          // save token projectId
          token && this.setToken(token)
          projectId && this.setProjectId(projectId)
          // set basic info
          await this.setBasicInfo()
          resolve()
        } catch (error) {
          console.log('login error:', error)
          reject(error)
        }
      })
      // return null
    },
    backWxLoginPage() {
      showToast({ message: '请登录后再访问~', onClose: navigateToLogin })
    },
    async urlTokenLogin() {
      return new Promise(async (resolve, reject) => {
        const localToken = getLocal('token')
        // 参数aes解密
        const { url, secret } = aes.DecryptQuery()
        this.setUrl(url)
        try {
          if (secret) {
            const { token, openid, projectId, appid, appVersion } = secret
            console.log('here secret')
            if (token && openid && projectId && appid) {
              const res = await loginByToken({ token, openid, projectId, appid, appVersion })
              //set base info
              await this.setToken(res.token)
              await this.setProjectId(res.projectId)
              this.setAppVersion(appVersion)
              this.setSecretDate(new Date().toISOString().substr(0, 10))
              await this.setBasicInfo()
              resolve()
            } else {
              this.backWxLoginPage()
              reject()
            }
          } else {
            if (localToken) resolve()
            else {
              this.backWxLoginPage()
              reject()
            }
          }
        } catch (error) {
          this.backWxLoginPage()
          reject()
        }
      })
    },

    logout() {
      this.reset()
    },
    async setBasicInfo() {
      //设置项目，微信信息
      const info = await getInfo()
      this.setProjectInfo(info.projectInfo)
      this.setWxInfo(info.wxInfo || {})
      this.setSettingInfo(info.settingInfo || {})
      //设置皮肤
      const storeTheme = await getStoreTheme()
      this.setTheme({ ...storeTheme.storeThemeStyle, PrimaryButtonColor: '#FD7F5C', PrimaryFontColor: '#FD7F5C' })
      //设置用户信息
      const userInfo = await getUserInfo()
      this.setUserInfo(userInfo)
      const { list } = await getAreaCodeListApi()
      this.setAreaCodeList(JSON.stringify(list))
    }
  }
})
